import IFilterSettings, { CompanyMinLocationCount, CreatedWithin, ICompanyFilterSettings, IDealFilterSettings, ILeadFilterSettings, ILeaseCompFilterSettings, ISaleCompFilterSettings, ITenantInMarketFilterSettings } from "../interfaces/IFilterSettings";
import { LeadStatus } from "../interfaces/ILead";
import { LeaseCompPropertyType } from "../interfaces/ILeaseComp";
import { SaleCompPropertyType } from "../interfaces/ISaleComp";

export const getDefaultFilters = (): IFilterSettings => ({
    dealSettings: {
        minValue: null,
        maxValue: null,
        minBuildingSize: null,
        maxBuildingSize: null,
        minLandSize: null,
        maxLandSize: null,
        pipedrivePersonName: "",
        keyword: "",
        statuses: [],
        dealStageIds: [],
        labelIds: [],
        hideSold: false,
        hideNonFund: false,
        minDateModified: null,
        maxDateModified: null,
    },
    leadSettings: {
        minLotCoverage: null,
        maxLotCoverage: null,
        minBuildingSize: null,
        maxBuildingSize: null,
        minLandSize: null,
        maxLandSize: null,
        minUsableArea: null,
        maxUsableArea: null,
        statuses: [LeadStatus.OpenLead, LeadStatus.ActiveConversations],
        showWithReminder: true,
        showWithoutReminder: true,
        labelIds: [],
        locationGrades: [],
        propertyGrades: [],
        ownershipTypes: [],
        keyword: "",
        minDateModified: null,
        maxDateModified: null,
    },
    leaseCompSettings: getDefaultLeaseCompFilters(),
    saleCompSettings: getDefaultSaleCompFilters(),
    tenantInMarketSettings: getDefaultTenantInMarketFilters(),
    companySettings: getDefaultCompanyFilters(),
});

export const getDefaultLeaseCompFilters = (): ILeaseCompFilterSettings => ({
    minTenantLandSize: null,
    maxTenantLandSize: null,
    minBuildingSize: null,
    maxBuildingSize: null,
    minAnnualRent: null,
    maxAnnualRent: null,
    minLeaseExpiration: null,
    maxLeaseExpiration: null,
    minDateModified: null,
    maxDateModified: null,
    propertyTypes: Object.values(LeaseCompPropertyType),
});

export const getDefaultSaleCompFilters = (): ISaleCompFilterSettings => ({
    minUsableLandSize: null,
    maxUsableLandSize: null,
    minParcelSize: null,
    maxParcelSize: null,
    minBuildingSize: null,
    maxBuildingSize: null,
    minSalePrice: null,
    maxSalePrice: null,
    minSaleDate: null,
    maxSaleDate: null,
    minDateModified: null,
    maxDateModified: null,
    propertyTypes: Object.values(SaleCompPropertyType),
});

export const getDefaultTenantInMarketFilters = (): ITenantInMarketFilterSettings => ({
    createdWithin: CreatedWithin.Anytime,
    showInactive: false,
    showLostDeals: true,
});

export const fillMissingFilters = (filterSettings: IFilterSettings): IFilterSettings => {
    if (!filterSettings.dealSettings.statuses)
        filterSettings.dealSettings.statuses = [];

    if (!filterSettings.dealSettings.dealStageIds)
        filterSettings.dealSettings.dealStageIds = [];

    if (!filterSettings.dealSettings.labelIds)
        filterSettings.dealSettings.labelIds = [];

    if (!filterSettings.leadSettings.statuses)
        filterSettings.leadSettings.statuses = [];

    filterSettings.leadSettings.statuses = filterSettings.leadSettings.statuses.filter(s => Object.values(LeadStatus).includes(s));

    if (filterSettings.leadSettings.showWithReminder === undefined)
        filterSettings.leadSettings.showWithReminder = true;

    if (filterSettings.leadSettings.showWithoutReminder === undefined)
        filterSettings.leadSettings.showWithoutReminder = true;

    if (!filterSettings.leadSettings.labelIds)
        filterSettings.leadSettings.labelIds = [];

    if (!filterSettings.leadSettings.locationGrades)
        filterSettings.leadSettings.locationGrades = [];

    if (!filterSettings.leadSettings.propertyGrades)
        filterSettings.leadSettings.propertyGrades = [];

    if (!filterSettings.leadSettings.ownershipTypes)
        filterSettings.leadSettings.ownershipTypes = [];

    if (!filterSettings.leaseCompSettings.propertyTypes)
        filterSettings.leaseCompSettings.propertyTypes = [];

    if (!filterSettings.saleCompSettings.propertyTypes)
        filterSettings.saleCompSettings.propertyTypes = [];

    return filterSettings;
}

export const getDefaultCompanyFilters = (): ICompanyFilterSettings => ({
    includeSaleComps: true,
    includeLeaseComps: true,
    hideDuplicateLocations: true,
    minLocationCount: CompanyMinLocationCount.All,
    hideNoLogoCompany: false,
});

export const areDealFiltersDifferent = (dealFilters: IDealFilterSettings, anotherDealFilters: IDealFilterSettings) => {
    return dealFilters.minValue !== anotherDealFilters.minValue
        || dealFilters.maxValue !== anotherDealFilters.maxValue
        || dealFilters.minBuildingSize !== anotherDealFilters.minBuildingSize
        || dealFilters.maxBuildingSize !== anotherDealFilters.maxBuildingSize
        || dealFilters.minLandSize !== anotherDealFilters.minLandSize
        || dealFilters.maxLandSize !== anotherDealFilters.maxLandSize
        || dealFilters.pipedrivePersonName !== anotherDealFilters.pipedrivePersonName
        || dealFilters.keyword !== anotherDealFilters.keyword
        || dealFilters.statuses.length !== anotherDealFilters.statuses.length
        || dealFilters.statuses.filter(s => anotherDealFilters.statuses.includes(s)).length !== dealFilters.statuses.length
        || dealFilters.dealStageIds.length !== anotherDealFilters.dealStageIds.length
        || dealFilters.dealStageIds.filter(ds => anotherDealFilters.dealStageIds.includes(ds)).length !== dealFilters.dealStageIds.length
        || dealFilters.labelIds.length !== anotherDealFilters.labelIds.length
        || dealFilters.labelIds.filter(ds => anotherDealFilters.labelIds.includes(ds)).length !== dealFilters.labelIds.length
        || dealFilters.hideSold !== anotherDealFilters.hideNonFund
        || dealFilters.hideNonFund !== anotherDealFilters.hideNonFund
}

export const areLeadFiltersDifferent = (leadFilters: ILeadFilterSettings, anotherLeadFilters: ILeadFilterSettings) => {
    return leadFilters.minLotCoverage !== anotherLeadFilters.minLotCoverage
        || leadFilters.maxLotCoverage !== anotherLeadFilters.maxLotCoverage
        || leadFilters.minBuildingSize !== anotherLeadFilters.minBuildingSize
        || leadFilters.maxBuildingSize !== anotherLeadFilters.maxBuildingSize
        || leadFilters.minLandSize !== anotherLeadFilters.minLandSize
        || leadFilters.maxLandSize !== anotherLeadFilters.maxLandSize
        || leadFilters.minUsableArea !== anotherLeadFilters.minUsableArea
        || leadFilters.maxUsableArea !== anotherLeadFilters.maxUsableArea
        || leadFilters.keyword !== anotherLeadFilters.keyword
        || leadFilters.statuses.length !== anotherLeadFilters.statuses.length
        || leadFilters.statuses.filter(s => anotherLeadFilters.statuses.includes(s)).length !== leadFilters.statuses.length
        || leadFilters.showWithReminder !== anotherLeadFilters.showWithReminder
        || leadFilters.showWithoutReminder !== anotherLeadFilters.showWithoutReminder
        || leadFilters.labelIds.length !== anotherLeadFilters.labelIds.length
        || leadFilters.labelIds.filter(id => anotherLeadFilters.labelIds.includes(id)).length !== leadFilters.labelIds.length
        || leadFilters.locationGrades.length !== anotherLeadFilters.locationGrades.length
        || leadFilters.locationGrades.filter(g => anotherLeadFilters.locationGrades.includes(g)).length !== leadFilters.locationGrades.length
        || leadFilters.propertyGrades.length !== anotherLeadFilters.propertyGrades.length
        || leadFilters.propertyGrades.filter(g => anotherLeadFilters.propertyGrades.includes(g)).length !== leadFilters.propertyGrades.length
        || leadFilters.ownershipTypes.length !== anotherLeadFilters.ownershipTypes.length
        || leadFilters.ownershipTypes.filter(ot => anotherLeadFilters.ownershipTypes.includes(ot)).length !== leadFilters.ownershipTypes.length
}

export const areLeaseCompFiltersDifferent = (leaseCompFilters: ILeaseCompFilterSettings, anotherLeaseCompFilters: ILeaseCompFilterSettings) => {
    return leaseCompFilters.minTenantLandSize !== anotherLeaseCompFilters.minTenantLandSize
        || leaseCompFilters.maxTenantLandSize !== anotherLeaseCompFilters.maxTenantLandSize
        || leaseCompFilters.minBuildingSize !== anotherLeaseCompFilters.minBuildingSize
        || leaseCompFilters.maxBuildingSize !== anotherLeaseCompFilters.maxBuildingSize
        || leaseCompFilters.minAnnualRent !== anotherLeaseCompFilters.minAnnualRent
        || leaseCompFilters.maxAnnualRent !== anotherLeaseCompFilters.maxAnnualRent
        || leaseCompFilters.minLeaseExpiration !== anotherLeaseCompFilters.minLeaseExpiration
        || leaseCompFilters.maxLeaseExpiration !== anotherLeaseCompFilters.maxLeaseExpiration
        || leaseCompFilters.propertyTypes.length !== anotherLeaseCompFilters.propertyTypes.length
        || leaseCompFilters.propertyTypes.filter(pt => anotherLeaseCompFilters.propertyTypes.includes(pt)).length !== leaseCompFilters.propertyTypes.length
}

export const areSaleCompFiltersDifferent = (saleCompFilters: ISaleCompFilterSettings, anotherSaleCompFilters: ISaleCompFilterSettings) => {
    return saleCompFilters.minUsableLandSize !== anotherSaleCompFilters.minUsableLandSize
        || saleCompFilters.maxUsableLandSize !== anotherSaleCompFilters.maxUsableLandSize
        || saleCompFilters.minParcelSize !== anotherSaleCompFilters.minParcelSize
        || saleCompFilters.maxParcelSize !== anotherSaleCompFilters.maxParcelSize
        || saleCompFilters.minBuildingSize !== anotherSaleCompFilters.minBuildingSize
        || saleCompFilters.maxBuildingSize !== anotherSaleCompFilters.maxBuildingSize
        || saleCompFilters.minSalePrice !== anotherSaleCompFilters.minSalePrice
        || saleCompFilters.maxSalePrice !== anotherSaleCompFilters.maxSalePrice
        || saleCompFilters.minSaleDate !== anotherSaleCompFilters.minSaleDate
        || saleCompFilters.maxSaleDate !== anotherSaleCompFilters.maxSaleDate
        || saleCompFilters.propertyTypes.length !== anotherSaleCompFilters.propertyTypes.length
        || saleCompFilters.propertyTypes.filter(pt => anotherSaleCompFilters.propertyTypes.includes(pt)).length !== saleCompFilters.propertyTypes.length
}

export const areTenantInMarketFiltersDifferent = (tenantInMarketFilters: ITenantInMarketFilterSettings, anotherTenantInMarketFilters: ITenantInMarketFilterSettings) => {
    return tenantInMarketFilters.createdWithin !== anotherTenantInMarketFilters.createdWithin
        || tenantInMarketFilters.showInactive !== anotherTenantInMarketFilters.showInactive
        || tenantInMarketFilters.showLostDeals !== anotherTenantInMarketFilters.showLostDeals
}

export const areCompanyFiltersDifferent = (companyFilters: ICompanyFilterSettings, anotherCompanyFilters: ICompanyFilterSettings) => {
    return companyFilters.includeSaleComps !== anotherCompanyFilters.includeSaleComps ||
        companyFilters.includeLeaseComps !== anotherCompanyFilters.includeLeaseComps ||
        companyFilters.hideDuplicateLocations !== anotherCompanyFilters.hideDuplicateLocations ||
        companyFilters.hideNoLogoCompany !== anotherCompanyFilters.hideNoLogoCompany ||
        companyFilters.minLocationCount !== anotherCompanyFilters.minLocationCount 
}
